import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, Divider, Paper,
  Menu, MenuItem, TextField, InputAdornment, ListItemIcon, ListItemText
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { apiClient, apiClientRaw } from '../utils/api';
import { toast } from 'react-toastify';
 
import { clearAuthData , updateTocAccepted } from '../../store/slices/authSlice';
import { resetLawfirm } from '../../store/slices/lawFirmSlice';
import { persistor } from '../../store/store';

const LawFirmDashboard = ({ title, links, sectionTitle }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => state.auth.user);
  const refreshToken = localStorage.getItem('refreshToken');

  const [openTerms, setOpenTerms] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTitle, setMenuTitle] = useState('');
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuSearch, setMenuSearch] = useState('');

  // Normalize links to a flat list for rendering; supports legacy nested arrays
  const normalizedLinks = Array.isArray(links)
    ? (Array.isArray(links[0]) ? links.flat() : links)
    : [];

  const displayedSectionTitle = sectionTitle || (title && /law\s*f?irm/i.test(String(title)) ? 'Law Firm Actions' : 'Quick Actions');

  // Support grouped structure: [{ action_value, values: [ { name, path, icon } ] }]
  const isGroupedStructure = Array.isArray(links)
    && links.length > 0
    && !Array.isArray(links[0])
    && typeof links[0] === 'object'
    && Array.isArray(links[0].values);

  const groups = isGroupedStructure
    ? links.map((group) => ({
        title: group?.action_value || displayedSectionTitle,
        description: group?.action_description,
        items: Array.isArray(group?.values) ? group.values : [],
      }))
    : [{ title: displayedSectionTitle, items: normalizedLinks }];

  const handleActionClick = (link, event) => {
    const hasOptions = Array.isArray(link?.options) && link.options.length > 0;
    if (hasOptions) {
      setMenuAnchorEl(event.currentTarget);
      setMenuOptions(link.options);
      setMenuTitle(link.name || 'Options');
      setMenuSearch('');
      return;
    }
    if (link?.path) navigate(link.path);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuOptions([]);
    setMenuTitle('');
    setMenuSearch('');
  };

  useEffect(() => {
    if (userdetails && userdetails.toc_accepted === false) {
      setOpenTerms(true);
    }
  }, [userdetails]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleAccept = async () => {
    try {
      await apiClient('/toc-accept', 'PATCH');
      toast.success('Terms accepted successfully.');
      dispatch(updateTocAccepted());  // ✅ update Redux state
      setOpenTerms(false);
    } catch (error) {
      toast.error('Failed to accept terms. Please try again.');
    }
  };
  
  const handleReject = async () => {
    setLoggingOut(true);
    // Remove all sensitive data and tokens
    dispatch(clearAuthData());
    dispatch(resetLawfirm());
    localStorage.removeItem('accessToken');
    localStorage.removeItem('lastVisitedPath');
    localStorage.removeItem('count');
    if (persistor && persistor.purge) {
      persistor.purge();
    }
    navigate('/');
    toast.info('You must accept the Terms to continue. You have been logged out.');

    try {
      const response = await apiClientRaw('/logout/', 'POST', { refresh: refreshToken });

      if (response.status === 205) {
        // Already cleared above
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <Box p={3} gap={3} display="flex" flexDirection="column" alignItems="center">
      <Typography variant="h6" fontFamily="sans-serif" fontWeight="500" mb={2} sx={{ color: 'black' }}>
        {title}
      </Typography>

      <Box
        width="100%"
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        sx={{ pb: 1 }}
      >
        {groups.map((group, gIdx) => (
          <Paper
            key={gIdx}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 1,
              background: (theme) => (theme.palette.mode === 'light' ? 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)' : theme.palette.background.paper),
              border: '1px solid rgba(37, 99, 235, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              flex: 1,
              minWidth: 0
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#333333' }}>
                {group.title}
              </Typography>
              {/* Spacer to keep layout clean if we expand controls later */}
            </Box>
            {group.description ? (
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 1.5 }}>
                {group.description}
              </Typography>
            ) : null}
            <Box
              display="flex"
              flexDirection="row"
              sx={{
                gap: { xs: 1, sm: 1.5 },
                flexWrap: 'wrap',
                pb: 0.5,
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}
            >
              {group.items.map((link, idx) => (
                <Button
                  key={idx}
                  variant="outlined"
                  size="medium"
                  onClick={(e) => handleActionClick(link, e)}
                  startIcon={
                    link.icon ? React.cloneElement(link.icon, { style: { fontSize: 20 } }) : null
                  }
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: { xs: 1.5, sm: 2.5 },
                    py: { xs: 0.75, sm: 1.25 },
                    color: '#2563eb',
                    borderColor: 'rgba(37, 99, 235, 0.3)',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {link.name}
                </Button>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Options dropdown menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            minWidth: 320,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
          },
        }}
      >
        <Box px={2} pt={2} pb={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {menuTitle}
          </Typography>
          <TextField
            value={menuSearch}
            onChange={(e) => setMenuSearch(e.target.value)}
            placeholder="Search..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
          {menuOptions
            .filter((opt) =>
              !menuSearch || String(opt?.name || '').toLowerCase().includes(menuSearch.toLowerCase())
            )
            .map((opt, i) => (
              <MenuItem
                key={`${opt?.name || 'option'}-${i}`}
                onClick={() => {
                  if (opt?.path) navigate(opt.path);
                  handleCloseMenu();
                }}
              >
                {opt?.icon ? (
                  <ListItemIcon>
                    {React.cloneElement(opt.icon, { fontSize: 'small' })}
                  </ListItemIcon>
                ) : null}
                <ListItemText primary={opt?.name || 'Open'} />
              </MenuItem>
            ))}
          {menuOptions.filter((opt) =>
            !menuSearch || String(opt?.name || '').toLowerCase().includes(menuSearch.toLowerCase())
          ).length === 0 ? (
            <Box px={2} py={1}>
              <Typography variant="body2" color="text.secondary">No matches</Typography>
            </Box>
          ) : null}
        </Box>
      </Menu>


      <Dialog
        open={openTerms}
        onClose={() => {}}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          style: {
            borderRadius: '16px',
            padding: '10px',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
          Website Terms of Use
        </DialogTitle>
        <Divider />
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Website Terms of Use" />
          <Tab label="Privacy Policy for California Residents" />
        </Tabs>
        <DialogContent
          dividers
          sx={{ height: '70vh', p: 2, backgroundColor: '#fafafa' }}
        >
          {selectedTab === 0 && (
            <iframe
              src="/toc.pdf"
              title="Terms of Use PDF"
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
            />
          )}
          {selectedTab === 1 && (
            <iframe
              src="/ccpa.pdf"
              title="Privacy Policy PDF"
              style={{ width: '100%', height: '100%', border: 'none', borderRadius: '4px' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Documents provided by Vermost & Associates, LLC
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              onClick={handleReject}
              variant="outlined"
              color="error"
              disabled={loggingOut}
            >
              Reject
            </Button>
            <Button
              onClick={handleAccept}
              variant="contained"
              color="primary"
            >
              Accept
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LawFirmDashboard;
